import React, { useId, PropsWithChildren, ReactNode } from "react";
import cx from "classnames";
import DOMPurify from "dompurify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { dataTestIds } from "data-testids";
import { useIsMobile } from "../../../generics/dom-extensions";
import "./styles.scss";

export interface TooltipProps {
  title?: string;
  html?: string;
  content?: ReactNode;
  datatestid?: string;
  disabled?: boolean;
  tooltipClassName?: string;
  className?: string;
}

const handleHtmlClick = (event: React.MouseEvent<HTMLParagraphElement>) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest<HTMLAnchorElement>("a[href]");
  const href = link?.getAttribute("href")?.trim();

  if (!href) return;

  event.preventDefault();

  window.open(href, "_blank", "noopener,noreferrer");
};

const Tooltip = ({
  title,
  html,
  content,
  children,
  disabled,
  datatestid,
  className,
  tooltipClassName,
}: PropsWithChildren<TooltipProps>) => {
  const id = useId();
  const isMobile = useIsMobile();

  if (disabled) return <>{children}</>;

  return (
    <>
      {children && (
        <span
          data-tooltip-id={id}
          className={className}
          data-testid={dataTestIds.componentLibrary["Molecules.Tooltip.Icon"]}
        >
          {children}
        </span>
      )}
      <ReactTooltip
        id={id}
        clickable
        className={cx("tooltip", tooltipClassName)}
        opacity={0.84}
        openOnClick={isMobile}
        globalCloseEvents={{
          escape: true,
          scroll: true,
          clickOutsideAnchor: true,
        }}
        portalRoot={document.body}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- This wrapper only prevents tooltip clicks from reaching the trigger. */}
        <div
          data-testid={datatestid}
          onClick={(event) => event.stopPropagation()}
        >
          {title && <p className="font-semibold">{title}</p>}
          {html ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- Delegates clicks from native links in sanitized HTML.
            <p
              className="whitespace-pre-line break-words font-normal"
              onClick={handleHtmlClick}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            />
          ) : (
            <p className="break-words font-normal">{content}</p>
          )}
        </div>
      </ReactTooltip>
    </>
  );
};

export default Tooltip;
